import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getCuotaConfigs, createCuotaConfig, deleteCuotaConfig, generateCuotas,
  getCuotas, updateCuotaPago, getCuotasSummary, getMorosos, getRoles, exportCuotas
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Coins, AlertTriangle, CheckCircle, Clock, Receipt, Users, Settings, Download } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  pagado: { label: 'Pagado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  parcial: { label: 'Parcial', color: 'bg-blue-100 text-blue-700', icon: Receipt },
  exento: { label: 'Exento', color: 'bg-stone-100 text-stone-700', icon: CheckCircle },
};

export default function Cuotas() {
  const { hasPermission } = useAuth();
  const { formatCurrency } = useNotifications();
  const [configs, setConfigs] = useState([]);
  const [cuotas, setCuotas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [morosos, setMorosos] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateConfig, setShowCreateConfig] = useState(false);
  const [showGenerate, setShowGenerate] = useState(null);
  const [selectedPeriodo, setSelectedPeriodo] = useState('');
  const [selectedConfig, setSelectedConfig] = useState('');
  const [generatePeriodo, setGeneratePeriodo] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [configForm, setConfigForm] = useState({
    nombre: '', monto: '', periodicidad: 'mensual', descripcion: '', aplica_roles: []
  });

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchCuotas(); }, [selectedPeriodo, selectedConfig]);

  const fetchData = async () => {
    try {
      const [configsRes, summaryRes, morososRes, rolesRes] = await Promise.all([
        getCuotaConfigs(), getCuotasSummary(), getMorosos(), getRoles()
      ]);
      setConfigs(configsRes.data);
      setSummary(summaryRes.data);
      setMorosos(morososRes.data);
      setRoles(rolesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCuotas = async () => {
    try {
      const params = {};
      if (selectedPeriodo) params.periodo = selectedPeriodo;
      if (selectedConfig) params.config_id = selectedConfig;
      const res = await getCuotas(params);
      setCuotas(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateConfig = async () => {
    if (!configForm.nombre || !configForm.monto) return toast.error('Nombre y monto son requeridos');
    try {
      await createCuotaConfig({ ...configForm, monto: parseFloat(configForm.monto) });
      toast.success('Cuota configurada');
      setShowCreateConfig(false);
      setConfigForm({ nombre: '', monto: '', periodicidad: 'mensual', descripcion: '', aplica_roles: [] });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleGenerate = async () => {
    if (!showGenerate || !generatePeriodo) return;
    try {
      const res = await generateCuotas(showGenerate.id, generatePeriodo);
      toast.success(res.data.message);
      setShowGenerate(null);
      fetchData();
      fetchCuotas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handlePago = async (cuotaId, estado) => {
    try {
      await updateCuotaPago(cuotaId, { estado });
      toast.success('Pago actualizado');
      fetchCuotas();
      fetchData();
    } catch (err) { toast.error('Error al actualizar pago'); }
  };

  if (!hasPermission('cuotas', 'ver')) {
    return <div className="text-center py-20 text-stone-500">Acceso denegado</div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const fmtCurrency = (v) => {
    try { return formatCurrency(v); } catch { return `${v}`; }
  };

  return (
    <div className="space-y-6" data-testid="cuotas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Cuotas y Pagos</h1>
          <p className="text-stone-500 text-sm">Gestión de cobros a miembros</p>
        </div>
        {hasPermission('cuotas', 'exportar') && (
          <Button variant="outline" onClick={async () => {
            try {
              const res = await exportCuotas(selectedPeriodo);
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = `cuotas_${selectedPeriodo || 'todos'}.csv`;
              document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
              toast.success('Exportación descargada');
            } catch { toast.error('Error al exportar'); }
          }} data-testid="export-cuotas-btn">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Total Recaudado</p>
                <p className="text-2xl font-bold text-emerald-700" data-testid="cuotas-recaudado">{fmtCurrency(summary?.total_recaudado || 0)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Total Pendiente</p>
                <p className="text-2xl font-bold text-amber-700" data-testid="cuotas-pendiente">{fmtCurrency(summary?.total_pendiente || 0)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Morosos</p>
                <p className="text-2xl font-bold text-red-700" data-testid="cuotas-morosos">{morosos.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="cuotas">
        <TabsList>
          <TabsTrigger value="cuotas"><Receipt className="w-4 h-4 mr-1" />Cuotas</TabsTrigger>
          <TabsTrigger value="morosos"><AlertTriangle className="w-4 h-4 mr-1" />Morosos</TabsTrigger>
          <TabsTrigger value="config"><Settings className="w-4 h-4 mr-1" />Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="cuotas">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <CardTitle className="text-base">Registro de Cuotas</CardTitle>
                <div className="flex gap-2">
                  <Select value={selectedPeriodo || 'all'} onValueChange={v => setSelectedPeriodo(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[160px]" data-testid="cuotas-filter-periodo">
                      <SelectValue placeholder="Periodo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {(summary?.periodos || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selectedConfig || 'all'} onValueChange={v => setSelectedConfig(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Tipo cuota" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {configs.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-stone-500">
                      <th className="pb-3 font-medium">Miembro</th>
                      <th className="pb-3 font-medium">Cuota</th>
                      <th className="pb-3 font-medium">Periodo</th>
                      <th className="pb-3 font-medium text-right">Monto</th>
                      <th className="pb-3 font-medium">Estado</th>
                      <th className="pb-3 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuotas.map(c => {
                      const est = ESTADOS[c.estado] || ESTADOS.pendiente;
                      return (
                        <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`cuota-row-${c.id}`}>
                          <td className="py-3 font-medium text-stone-700">{c.user_name}</td>
                          <td className="py-3 text-stone-600">{c.config_nombre}</td>
                          <td className="py-3 text-stone-600">{c.periodo}</td>
                          <td className="py-3 text-right font-medium">{fmtCurrency(c.monto)}</td>
                          <td className="py-3">
                            <Badge className={est.color}>{est.label}</Badge>
                          </td>
                          <td className="py-3 text-right">
                            {c.estado !== 'pagado' && hasPermission('cuotas', 'editar') && (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="outline" className="text-xs h-7"
                                  onClick={() => handlePago(c.id, 'pagado')} data-testid={`pay-cuota-${c.id}`}>
                                  Pagado
                                </Button>
                                <Button size="sm" variant="ghost" className="text-xs h-7"
                                  onClick={() => handlePago(c.id, 'exento')}>
                                  Exento
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {cuotas.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-stone-400">No hay cuotas para el periodo seleccionado</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="morosos">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />Miembros con Cuotas Pendientes</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-stone-500">
                      <th className="pb-3 font-medium">Miembro</th>
                      <th className="pb-3 font-medium text-center">Cuotas Pendientes</th>
                      <th className="pb-3 font-medium text-right">Deuda Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {morosos.map((m, i) => (
                      <tr key={i} className="border-b border-stone-100" data-testid={`moroso-${i}`}>
                        <td className="py-3 font-medium text-stone-700">{m.user_name}</td>
                        <td className="py-3 text-center"><Badge variant="secondary">{m.cuotas_pendientes}</Badge></td>
                        <td className="py-3 text-right font-bold text-red-700">{fmtCurrency(m.deuda_total)}</td>
                      </tr>
                    ))}
                    {morosos.length === 0 && (
                      <tr><td colSpan={3} className="py-8 text-center text-stone-400">No hay morosos</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Tipos de Cuota</CardTitle>
              {hasPermission('cuotas', 'crear') && (
                <Button size="sm" onClick={() => setShowCreateConfig(true)} data-testid="create-cuota-config-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nueva Cuota
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {configs.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`config-${c.id}`}>
                    <div>
                      <h4 className="font-medium">{c.nombre}</h4>
                      <p className="text-sm text-stone-500">{fmtCurrency(c.monto)} / {c.periodicidad}</p>
                      {c.descripcion && <p className="text-xs text-stone-400 mt-1">{c.descripcion}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowGenerate(c)} data-testid={`generate-cuotas-${c.id}`}>
                        Generar
                      </Button>
                    </div>
                  </div>
                ))}
                {configs.length === 0 && (
                  <p className="text-center text-stone-400 py-8">No hay cuotas configuradas. Crea una para empezar.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Config Dialog */}
      <Dialog open={showCreateConfig} onOpenChange={setShowCreateConfig}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva Configuración de Cuota</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={configForm.nombre} onChange={e => setConfigForm({...configForm, nombre: e.target.value})}
                placeholder="Ej: Cuota mensual" data-testid="config-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto *</Label>
                <Input type="number" step="0.01" value={configForm.monto}
                  onChange={e => setConfigForm({...configForm, monto: e.target.value})} data-testid="config-monto" />
              </div>
              <div className="space-y-2">
                <Label>Periodicidad</Label>
                <Select value={configForm.periodicidad} onValueChange={v => setConfigForm({...configForm, periodicidad: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensual">Mensual</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                    <SelectItem value="unica">Única</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input value={configForm.descripcion} onChange={e => setConfigForm({...configForm, descripcion: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateConfig(false)}>Cancelar</Button>
            <Button onClick={handleCreateConfig} data-testid="submit-config-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={!!showGenerate} onOpenChange={() => setShowGenerate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generar Cuotas: {showGenerate?.nombre}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-stone-600">Se generarán cuotas de {showGenerate && fmtCurrency(showGenerate.monto)} para todos los miembros aplicables.</p>
            <div className="space-y-2">
              <Label>Periodo (YYYY-MM)</Label>
              <Input value={generatePeriodo} onChange={e => setGeneratePeriodo(e.target.value)}
                placeholder="2026-03" data-testid="generate-periodo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerate(null)}>Cancelar</Button>
            <Button onClick={handleGenerate} data-testid="submit-generate-btn">Generar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
